/**
 * Shared hook for accessing and listening to workspace ID changes
 *
 * Provides:
 * - Current workspace ID from localStorage
 * - Automatic updates when workspace changes
 * - Single source of truth for workspace ID across the app
 */

import { useState, useEffect } from 'react';
import { CURRENT_WORKSPACE_ID } from '@/utils/local_storage_keys';

export function useWorkspaceId(): string | null {
  const [workspaceId, setWorkspaceId] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem(CURRENT_WORKSPACE_ID) : null
  );

  useEffect(() => {
    const handleWorkspaceChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ workspaceId: string }>;
      const newWorkspaceId = customEvent.detail.workspaceId;
      setWorkspaceId(newWorkspaceId);
    };

    // A workspace switch in another tab updates the shared localStorage key but
    // only fires `workspaceChanged` in that tab. Without this listener, this tab
    // keeps rendering the old workspace while request-time localStorage reads
    // (x-workspace-id headers) already point at the new one — queries silently
    // run in a workspace the UI isn't showing.
    const handleStorage = (e: StorageEvent) => {
      if (e.key === CURRENT_WORKSPACE_ID) {
        setWorkspaceId(e.newValue);
      }
    };

    window.addEventListener('workspaceChanged', handleWorkspaceChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('workspaceChanged', handleWorkspaceChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return workspaceId;
}
